import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {DjangoPage} from '../django-paginator';

export enum EmailStatus {
  STATUS_QUEUED = 0,
  STATUS_DELIVERED,
  STATUS_OPENED,
  STATUS_BOUNCED,
  STATUS_COMPLAINED,
  STATUS_DROPPED,
  STATUS_SENDING_FAILED,
  STATUS_PERMANENT_FAIL,
  STATUS_TEMPORARY_FAIL,
  STATUS_CLICKED,
  STATUS_IN_PROGRESS,
}

export interface VeryfiedEvents {
  count: number;
  next: string;
  previous: null;
  results: VerEvResult[];
}

export interface VerEvResult {
  id: number;
  name: string;
  profile_photo: string;
  start: Date;
  has_lineup: boolean;
  has_timetable: boolean;
  has_map: boolean;
  is_verified: boolean;
  is_highlighted: null;
  fb_id: string;
  address: VerEvAddress;
  venue: VerEvVenue;
  on_priority_list: boolean;
  lineup_complete: boolean;
  timetable_complete: boolean;
  has_valid_fb_id: boolean;
  visitors_count: number;
  followers_count: number;
  fb_attending_count: number;
  fb_interested_count: number;
  updated_at: Date;
  syncing_enabled: boolean;
  is_cancelled: boolean;
  published_at: null;
}

export interface VerEvAddress {
  id_address: number;
  zip_code: string;
  street: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
  is_dummy: boolean;
}

export interface VerEvVenue {
  id: number;
  name: string;
}

interface EventDetails {
  id: number;
  name: string;
  short_description: string;
  long_description: string;
  category: number | string;
  photo: string;
  secondary_photo: string;
  background_movie: string;
  photo_id: string | number;
  slider: [];
  map_photo: string;
  venue: string;
  venue_id: string | number;
  venue_name: string;
  location: {lat: 0; lon: 0};
  start: string;
  end: string;
  timezone: string | null;
  visitors_count: number;
  details: {variable_facebook_page: string};
  web_page: string;
  movie: string;
  mobile_page: string;
  ticket_link: string;
  areas: [];
  performances: any;
  performances_plans: [];
  address: string;
  is_highlighted: boolean | number;
  is_verified: boolean | number;
  timetable_days: [];
  ordering: number;
  event_map: any;
  updated_at: string;
  last_event_update: string;
  has_cashless_payment_provider: boolean;
  ui: string;
  url_extension: string;
  headers: [];
  is_user_checked_in: boolean;
  is_reviewed_already: boolean;
  is_followed: boolean;
  last_update: string;
  is_paid_partner: boolean;
  company: number;
}

export interface MusicStyles {
  results: MusicStylesResult[];
}

export interface MusicStylesResult {
  value: string;
  name: string;
}

export interface AreasResult {
  id: number;
  ordering: number;
  name: string;
  description: null;
  days: any[];
  music_style: null;
  music_styles: any[];
  latitude: null;
  longitude: null;
  event_map_image: null;
}

export interface EventAreas {
  count: number;
  next: null;
  previous: null;
  results: AreasResult[];
}

export interface EventVenue {
  id: number;
  name: string;
  thumbnail: string;
}

export interface EventDay {
  id: number;
  day: Date;
  start: string;
  end: string;
  performance_start: Date;
  performance_end: Date;
  pf_id: number;
}

export interface EventInfo {
  selected_language: string;
  nl: string;
}

export interface AddressModel {
  city: string;
  country: string;
  street: string;
  latitude: number;
  longitude: number;
  zip_code: string;
}

export interface EventData {
  id: number;
  pf_id: number;
  partyflock_page: string | null;
  fb_id: string;
  fb_page: null;
  name: string;
  subtitle: null;
  event_group: null;
  event_group_id: null;
  venue: EventVenue[];
  venue_id: number;
  ticket_link: null;
  start: Date;
  end: Date;
  timezone: string | null;
  created_at: Date;
  days: EventDay[];
  category: string;
  category_ids: number[];
  main_category_id: null;
  location: Location;
  address: AddressModel;
  info: EventInfo;
  movie: null;
  web_page: null;
  is_verified: boolean;
  is_cancelled: boolean;
  published_at: null;
  location_type: string;
  profile_photo: string;
  cover_photo: string;
  map_photo: null;
  lineup_complete: boolean;
  timetable_released: boolean;
  timetable_release_date: null;
  event_map_release_date: null;
  supported_languages: any[];
  generate_share_links: boolean;
  share_links: null;
  music_style: null;
  music_styles: string[];
  event_map: null;
  is_cancel_request_sent: boolean;
  partner_category: number;
  last_event_update: Date;
  is_managing_company: boolean;
  authorized_companies: any[];
  company: number;
  secondary_photo: string;
  external_ticket_link: null;
  event_alias_names: string;
  likes_count: number;
  site_url: string;
  ticketing_connected: boolean;
  pixels: any[];
  background_movie: null;
  partyflock_campaign_link: null;
  hide_interactive_event_map_popup: boolean;
}

@Injectable()
export class EventsApiService {
  constructor(private http: HttpClient) {}

  getCompanyIdEvent = (companyID, id) =>
    this.http.get<EventDetails>(`/rest-api/v1/companies/${companyID}/events/${id}`);

  getCompanyEvents = (companyID, params = null) => {
    return this.http
      .get<DjangoPage<any>>(`rest-api/v1/companies/${companyID}/events`, {
        params,
      })
      .pipe(map(res => res.results));
  };

  getRelatedEvents = eventId => this.http.get(`/rest-api/v1/admin/events/${eventId}/related`);

  editRelatedEvents = (fromEventId, toEventId, payload) =>
    this.http.put(`/rest-api/v1/admin/events/${fromEventId}/related`, {...payload, to_event: toEventId});

  createRelatedEvents = (fromEventId, toEventId, payload) =>
    this.http.post(`/rest-api/v1/admin/events/${fromEventId}/related`, {...payload, to_event: toEventId});

  deleteRelatedEvents = (fromEventId, toEventId) =>
    this.http.delete(`/rest-api/v1/admin/events/${fromEventId}/related`, {params: {to_event: toEventId}});

  getCompanyEventsBaseData = (companyID, params = null) => {
    return this.http.get(`rest-api/v1/companies/${companyID}/events/events_base_data`, {params});
  };

  getVerifiedEvents = params => {
    return this.http.get<DjangoPage<VeryfiedEvents>>(`/ambassadors/api/events/verified`, {params});
  };

  getAllEvents = (q?) => {
    return this.http.get<DjangoPage<any>>(`rest-api/v1/events/verified?q=${q ? q : '0'}`);
  };

  deleteVeryfiedEvent = eventID => {
    return this.http.delete(`rest-api/admin-panel/api/v1/admin/events/${eventID}`);
  };

  editEventUiConfig = (eventId, payload) => this.http.put(`/rest-api/v1/admin/event/ui-config/${eventId}`, payload);

  editCreateEventUiConfig = (eventId, payload) =>
    this.http.post(`/rest-api/v1/admin/event/ui-config/${eventId}/create_ui`, payload);

  requestAccess = (companyID, body) => {
    return this.http.post(`rest-api/v1/events/${companyID}/request-access`, body);
  };

  reportNewEvent = (companyID, body) => {
    return this.http.post(`rest-api/v1/events/${companyID}/report-new`, body);
  };

  deleteEvent = (companyID, eventID) => {
    return this.http.delete(`rest-api/v1/companies/${companyID}/events/${eventID}`);
  };

  getBlacklistedEvents = () => {
    return this.http.get(`rest-api/v1/events/blacklist`);
  };

  getCSMbyEventID = eventID => {
    return this.http.get(`rest-api/v1/events/get_csm/${eventID}`);
  };

  setBlacklistedEvent = id => {
    return this.http.post(`rest-api/v1/events/blacklist/${id}`, {});
  };

  removeBlacklistedEvent = id => {
    return this.http.delete(`rest-api/v1/events/blacklist/${id}`);
  };

  exportRefundList = id => this.http.get(`/rest-api/v1/events/${id}/export-refund-list`);
  exportRefundTickets = id => this.http.get(`rest-api/v1/events/${id}/export-refund-tickets`);

  getEvent = id => this.http.get<EventDetails>(`rest-api/v3/events/${id}`);

  getCompanyEvent = id => this.http.get<EventDetails>(`/rest-api/v1/companies/0/events/${id}`);

  getEventShops = id => this.http.get(`rest-api/v1/admin/event/${id}/shops`);

  postProductShops = (prodId, body) => {
    return this.http.post(`rest-api/v1/admin/product/${prodId}/shops`, body);
  };

  updateCancellationReason = (eventID, payload = null) => {
    return this.http.put(`rest-api/v1/companies/0/events/${eventID}/update_cancellation_reason`, payload);
  };

  toggleSyncEvent = (eventId, sync_field) => {
    return this.http.put(`/ambassadors/api/events/verified/${eventId}/sync`, {sync_field: sync_field});
  };

  syncEvent = eventId => {
    return this.http.post(`/ambassadors/api/events/verified/${eventId}/sync`, {});
  };

  syncEventFromET = eventId => {
    return this.http.post(`/rest-api/v1/admin/events/${eventId}/sync_et`, {});
  };

  updateEventPfId = (eventId, pfId: number | null) =>
    this.http.patch<{pf_id: number | null}>(`/rest-api/v1/admin/events/${eventId}/pf_id`, {pf_id: pfId});

  getEventById = id => this.http.get<EventDetails>(`rest-api/v1/companies/0/events/${id}`);

  updateEvent = (eventID, payload = null) => {
    return this.http.put(`/rest-api/v1/companies/0/events/${eventID}`, payload);
  };

  getEventFormOptions = () => this.http.get(`rest-api/v1/variables/event/form-options`);

  getEventDetails = eventID => this.http.get(`/rest-api/v1/companies/events/${eventID}/details`);

  saveEventDetails = (eventID, body) => this.http.put(`/rest-api/v1/companies/events/${eventID}/details`, body);

  getFBDescription = (companyID, eventID) =>
    this.http.get(`/rest-api/v1/companies/${companyID}/events/${eventID}/fb_description`);

  getPredictedMusicStyles = (companyID, eventID) =>
    this.http.get(`/rest-api/v1/companies/${companyID}/events/${eventID}/predicted_music_styles`);

  saveEventPhoto = (eventID, body) => {
    return this.http.put(`/rest-api/v1/events/${eventID}/photos/update_photo`, body);
  };

  changeEventProfilePhotoType = (eventID, body) => {
    return this.http.put(`/rest-api/v1/companies/events/${eventID}/profile_photo/type`, body);
  };

  putBackgroundMovie = (eventId, movie: string) =>
    this.http.put(`/rest-api/v1/admin/events/${eventId}/background_movie`, {movie});

  deleteBackgroundMovie = (eventId) =>
    this.http.delete(`/rest-api/v1/admin/events/${eventId}/background_movie`);

  getEventChatStatus = eventID => {
    return this.http.get(`rest-api/v1/companies/0/events/${eventID}/get_chat_status`);
  };

  toggleEventChatStatus = (eventID, status) => {
    return this.http.patch(`rest-api/v1/companies/0/events/${eventID}/toggle_chat_for_event`, {state: status});
  };

  pushEventToET = eventID => {
    return this.http.post(`/rest-api/v1/data-integrations/event_tool/push_to_et`, {ids: [eventID]});
  };

  pullEventFromET = eventID => {
    return this.http.post(`/rest-api/v1/data-integrations/event_tool/pull_from_et`, {ids: [eventID]});
  };

  removePerformancePhoto = (companyID, eventID, performanceId) =>
    this.http.put(
      `rest-api/v1/companies/${companyID}/events/${eventID}/performances/${performanceId}/remove_photo`,
      {},
    );

  getMusicStyles = () => this.http.get<MusicStyles>(`/rest-api/v1/variables/music_styles`);

  patchLineupReleaseById = (eventID, releaseId, body) =>
    this.http.patch(`rest-api/v1/events/${eventID}/lineup_release/${releaseId}`, body);

  createLineupRelease = (eventID, body?) => this.http.post(`rest-api/v1/events/${eventID}/lineup_release`, body);

  getCompanyAreas = (companyID, eventID) =>
    this.http.get<EventAreas>(`rest-api/v1/companies/${companyID}/events/${eventID}/areas`);

  getAllLineupPerformances = (eventID, params?) =>
    this.http.get(`rest-api/v1/events/${eventID}/lineup/get_all_performances`, {params});

  getLineupReleases = (eventID, params?) => this.http.get(`rest-api/v1/events/${eventID}/lineup_release`, {params});

  putPerfForLineupRelease = (eventID, releaseId, body?) =>
    this.http.put(`rest-api/v1/events/${eventID}/lineup_release/${releaseId}/update_performances_list`, body);

  deletePerformance = (companyID, eventID, performanceId) =>
    this.http.delete(`/rest-api/v1/companies/${companyID}/events/${eventID}/performances/${performanceId}`);

  deleteLineupReleaseById = (eventID, releaseId) =>
    this.http.delete(`rest-api/v1/events/${eventID}/lineup_release/${releaseId}`);

  putEventTimetable = (eventID, timetableId, body?) =>
    this.http.put(`rest-api/v1/events/${eventID}/timetable/${timetableId}`, body);

  getTimetablePreviewToken = (companyId, eventId) =>
    this.http.post(`/rest-api/v1/company/${companyId}/events/timetable/preview`, {event_id: eventId});

  getEventTimetable = (eventID, params?) => this.http.get(`rest-api/v1/events/${eventID}/timetable`, {params});

  getTimetableConfig = eventID => this.http.get(`/rest-api/v1/events/${eventID}/timetable/config`);

  createTimetable = (eventID, body) => this.http.post(`/rest-api/v1/events/${eventID}/timetable`, body);

  mergePerformances = (eventID, body) =>
    this.http.post(`/rest-api/v1/events/${eventID}/timetable/merge_performances`, body);

  updatePerformancesList = (eventID, body) =>
    this.http.put(`rest-api/v1/events/${eventID}/timetable/update_performances_list`, body);

  deleteTimetable = (eventID, timetableId) =>
    this.http.delete(`/rest-api/v1/events/${eventID}/timetable/${timetableId}`);

  createDescription = (eventId, body) => this.http.post(`/rest-api/v1/events/${eventId}/descriptions`, body);

  updateDescription = (eventId, itemId, body) =>
    this.http.put(`/rest-api/v1/events/${eventId}/descriptions/${itemId}`, body);

  getDescriptions = (eventId, language) =>
    this.http.get(`/rest-api/v1/events/${eventId}/descriptions?language=${language}`);

  getDescriptionsLanguages = eventId => this.http.get(`/rest-api/v1/events/${eventId}/descriptions/languages`);

  deleteDescriptions = (eventId, itemId) => this.http.delete(`/rest-api/v1/events/${eventId}/descriptions/${itemId}`);

  moveUpDescription = (eventId, itemId) =>
    this.http.put(`/rest-api/v1/events/${eventId}/descriptions/${itemId}/move_up`, {});

  moveDownDescription = (eventId, itemId) =>
    this.http.put(`/rest-api/v1/events/${eventId}/descriptions/${itemId}/move_down`, {});
}
